// Types: This file holds all action types.
import { ChangeEvent, FocusEvent, KeyboardEventHandler, MouseEvent } from 'react';
import { Dispatch } from 'redux';

import { AppState } from '../reducers';
import { CalculatorCacheActions } from './calculatorCache';
import {
    ApplicationActions,
    ConfigActions,
    ConsentActions,
    FeatureToggleActions,
    FeedbackActions,
    FetchOngoingApplicationActions,
    FormActions,
    KycActions,
    ProductsActions,
    StepsActions,
    UserActions,
    VehicleActions,
} from './index';
import { SblActions } from './sbl';

export enum actionTypes {
    KYC_BLOCKED_TOGGLE = 'KYC_BLOCKED_TOGGLE',
    // Config
    CONTEXT_PATH = 'kredittbank-securedloan-application-web/',
    PUBLIC_PATH = 'kredittbank-securedloan-application-web/public',
    RESOURCE_PATH = '/kredittbank-securedloan-application-web/rest/resource',
    LOAD_CONFIG = 'LOAD_CONFIG',
    OPEN_STEP = 'OPEN_STEP',
    UPDATE_STEPS_COMPLETION = 'UPDATE_STEPS_COMPLETION',

    // Feature Toggle
    FEATURE_TOGGLE_STARTED = 'FEATURE_TOGGLE_STARTED',
    FEATURE_TOGGLE_FULFILLED = 'FEATURE_TOGGLE_FULFILLED',
    FEATURE_TOGGLE_FAILED = 'FEATURE_TOGGLE_FAILED',

    // SBL consent
    CREATE_SBL_CONSENT_STARTED = 'CREATE_SBL_CONSENT_STARTED',
    CREATE_SBL_CONSENT_FULFILLED = 'CREATE_SBL_CONSENT_FULFILLED',
    CREATE_SBL_CONSENT_FAILED = 'CREATE_SBL_CONSENT_FAILED',
    FETCH_SBL_CONSENT_STARTED = 'FETCH_SBL_CONSENT_STARTED',
    FETCH_SBL_CONSENT_FULFILLED = 'FETCH_SBL_CONSENT_FULFILLED',
    FETCH_SBL_CONSENT_FAILED = 'FETCH_SBL_CONSENT_FAILED',

    // Fetch All Products
    FETCH_PRODUCTS_STARTED = 'FETCH_PRODUCTS_STARTED',
    FETCH_PRODUCTS_FULFILLED = 'FETCH_PRODUCTS_FULFILLED',
    FETCH_PRODUCTS_FAILED = 'FETCH_PRODUCTS_FAILED',

    // Fetch Product Info
    FETCH_PRODUCT_INFO_STARTED = 'FETCH_PRODUCT_INFO_STARTED',
    FETCH_PRODUCT_INFO_FULFILLED = 'FETCH_PRODUCT_INFO_FULFILLED',
    FETCH_PRODUCT_INFO_FAILED = 'FETCH_PRODUCT_INFO_FAILED',
    FETCH_PRODUCT_INFO_INVALID = 'FETCH_PRODUCT_INFO_INVALID',

    // Update Product
    UPDATE_PRODUCT = 'UPDATE_PRODUCT',

    // Fetch Vehicle Info
    FETCH_VEHICLE_INFO_STARTED = 'FETCH_VEHICLE_INFO_STARTED',
    FETCH_VEHICLE_INFO_FULFILLED = 'FETCH_VEHICLE_INFO_FULFILLED',
    FETCH_VEHICLE_INFO_FAILED = 'FETCH_VEHICLE_INFO_FAILED',

    // Fetch Ongoing Applications
    FETCH_ONGOING_APPLICATIONS_STARTED = 'FETCH_ONGOING_APPLICATIONS_STARTED',
    FETCH_ONGOING_APPLICATIONS_FULFILLED = 'FETCH_ONGOING_APPLICATIONS_FULFILLED',
    FETCH_ONGOING_APPLICATIONS_FAILED = 'FETCH_ONGOING_APPLICATIONS_FAILED',
    FETCH_ONGOING_APPLICATIONS_EUROTAX_CLEAR = 'FETCH_ONGOING_APPLICATIONS_EUROTAX_CLEAR',
    FETCH_ONGOING_APPLICATIONS_EUROTAX_FULFILLED = 'FETCH_ONGOING_APPLICATIONS_EUROTAX_FULFILLED',
    FETCH_ONGOING_APPLICATIONS_EUROTAX_FAILED = 'FETCH_ONGOING_APPLICATIONS_EUROTAX_FAILED',
    FETCH_OBJECT_CALCULATOR_DATA_FULFILLED = 'FETCH_OBJECT_CALCULATOR_DATA_FULFILLED',

    // Upgrade Ongoing Application to Loan
    UPGRADE_ONGOING_APPLICATION_STARTED = 'UPGRADE_ONGOING_APPLICATION_STARTED',
    UPGRADE_ONGOING_APPLICATION_FAILED = 'UPGRADE_ONGOING_APPLICATION_FAILED',
    UPGRADE_ONGOING_APPLICATION_SUCCESS = 'UPGRADE_ONGOING_APPLICATION_SUCCESS',

    // Fetch User Session
    FETCH_USER_SESSION_STARTED = 'FETCH_USER_SESSION_STARTED',
    FETCH_USER_SESSION_FULFILLED = 'FETCH_USER_SESSION_FULFILLED',
    FETCH_USER_SESSION_FAILED = 'FETCH_USER_SESSION_FAILED',

    // Fetch User Context
    FETCH_USER_CONTEXT_STARTED = 'FETCH_USER_CONTEXT_STARTED',
    FETCH_USER_CONTEXT_FULFILLED = 'FETCH_USER_CONTEXT_FULFILLED',
    FETCH_USER_CONTEXT_FAILED = 'FETCH_USER_CONTEXT_FAILED',

    // Update Calculator Cache
    UPDATE_CALCULATOR_CACHE_STARTED = 'UPDATE_CALCULATOR_CACHE_STARTED',
    UPDATE_CALCULATOR_CACHE_FULFILLED = 'UPDATE_CALCULATOR_CACHE_FULFILLED',
    UPDATE_CALCULATOR_CACHE_FAILED = 'UPDATE_CALCULATOR_CACHE_FAILED',

    // Fetch Calculator Cache
    FETCH_CALCULATOR_CACHE_STARTED = 'FETCH_CALCULATOR_CACHE_STARTED',
    FETCH_CALCULATOR_CACHE_FULFILLED = 'FETCH_CALCULATOR_CACHE_FULFILLED',
    FETCH_CALCULATOR_CACHE_FAILED = 'FETCH_CALCULATOR_CACHE_FAILED',

    // Fetch Ongoing Application
    FETCH_APPLICATION_STARTED = 'FETCH_APPLICATION_STARTED',
    FETCH_APPLICATION_FULFILLED = 'FETCH_APPLICATION_FULFILLED',
    FETCH_APPLICATION_FAILED = 'FETCH_APPLICATION_FAILED',

    // Fetch KYC
    FETCH_CUSTOMER_KYC_STARTED = 'FETCH_CUSTOMER_KYC_STARTED',
    FETCH_CUSTOMER_KYC_FULFILLED = 'FETCH_CUSTOMER_KYC_FULFILLED',
    FETCH_CUSTOMER_KYC_FAILED = 'FETCH_CUSTOMER_KYC_FAILED',
    SET_KYC_ANSWERED = 'SET_KYC_ANSWERED',
    KYC_ERROR = 'KYC_ERROR',
    KYC_SKIP = 'KYC_SKIP',

    // Fetch & Save Consent
    FETCH_CONSENT_STARTED = 'FETCH_CONSENT_STARTED',
    FETCH_CONSENT_FULFILLED = 'FETCH_CONSENT_FULFILLED',
    FETCH_CONSENT_FAILED = 'FETCH_CONSENT_FAILED',
    UPDATE_CONSENT = 'UPDATE_CONSENT',
    SAVE_CONSENT_STARTED = 'SAVE_CONSENT_STARTED',
    SAVE_CONSENT_FULFILLED = 'SAVE_CONSENT_FULFILLED',
    SAVE_CONSENT_FAILED = 'SAVE_CONSENT_FAILED',

    // Send Application
    SEND_APPLICATION_STARTED = 'SEND_APPLICATION_STARTED',
    SEND_APPLICATION_FULFILLED = 'SEND_APPLICATION_FULFILLED',
    SEND_APPLICATION_FAILED = 'SEND_APPLICATION_FAILED',

    // Send Feedback
    SEND_FEEDBACK_STARTED = 'SEND_FEEDBACK_STARTED',
    SEND_FEEDBACK_FULFILLED = 'SEND_FEEDBACK_FULFILLED',
    SEND_FEEDBACK_FAILED = 'SEND_FEEDBACK_FAILED',

    // Update Ongoing Application
    UPDATE_APPLICATION_STARTED = 'UPDATE_APPLICATION_STARTED',
    UPDATE_APPLICATION_FULFILLED = 'UPDATE_APPLICATION_FULFILLED',
    UPDATE_APPLICATION_FAILED = 'UPDATE_APPLICATION_FAILED',

    // Form Types Etc.
    UPDATE_LOAN_TYPE = 'UPDATE_LOAN_TYPE',
    FORM_VEHICLE_INFO = 'FORM_VEHICLE_INFO',
    FORM_LOAN_INFO = 'FORM_LOAN_INFO',
    FORM_APPLICATION_MAIN_APPLICANT = 'FORM_APPLICATION_MAIN_APPLICANT',
    FORM_APPLICATION_CO_APPLICANT = 'FORM_APPLICATION_CO_APPLICANT',
    TOGGLE_CO_APPLICANT = 'TOGGLE_CO_APPLICANT',
    FORM_COMPLETION_DETAILS = 'FORM_COMPLETION_DETAILS',
    FIELD_UPDATE_INSURANCE = 'FIELD_UPDATE_INSURANCE',
}

/* Redux Action */
type ReduxAction = import('redux').Action;

// @ts-ignore
export interface Action<T> extends ReduxAction {
    type: T;
}

/* Typed Actions */
export type AppActions =
    | UserActions
    | ProductsActions
    | CalculatorCacheActions
    | StepsActions
    | FormActions
    | ConfigActions
    | KycActions
    | VehicleActions
    | ConsentActions
    | ApplicationActions
    | FetchOngoingApplicationActions
    | FeatureToggleActions
    | SblActions
    | FeedbackActions;

/* Dispatching */
export type AsyncDispatch = (dispatch: Dispatch) => Promise<void>;
export type DispatchWithState = (dispatch: Dispatch<AppActions>, getState: () => AppState) => void;

/* Action Events */
export type InputEvent = ChangeEvent<HTMLInputElement>;
export type InputMouseEvent = MouseEvent<HTMLInputElement>;
export type InputBlurEvent = FocusEvent<HTMLInputElement>;
export type KeyboardEvent = KeyboardEventHandler<HTMLInputElement>;

export type SelectEvent = ChangeEvent<HTMLSelectElement>;
export type ButtonEvent = ChangeEvent<HTMLButtonElement>;
export type ButtonGroupChangeEvent = (e: { name: string; value: string | number }) => void;
export type ButtonGroupStringChangeEvent = (name: string, value: string) => void;
export type ButtonGroupNumberChangeEvent = (name: string, value: number) => void;

export interface StringKeyValue {
    [k: string]: string;
}

export interface StringKeyAnyValue {
    [k: string]: any;
}

export interface NameValueString {
    name: string;
    value: string;
}

export interface NameValueStringOrNumber {
    name: string;
    value: string | number;
}

export interface IndexNumber {
    index: number;
}
